<template>
  <PFRow :label="tagDefinition.name" size="12x12">
    <!-- TEXT -->
    <CInput
      v-if="tagDefinition.input_type === 'text'"
      :value="value"
      @input="$emit('input', $event)"
      :placeholder="placeholder"
      :maxlength="maxLength"
    />

    <!-- SELECT -->
    <PSelectKeyValue
      v-else-if="tagDefinition.input_type === 'select'"
      :value="value"
      @input="handleSelectInput"
      :options="selectOptions"
      label="label"
      track-by="value"
      allow-empty
    />

    <!-- NUMBER -->
    <CInput
      v-else-if="tagDefinition.input_type === 'number'"
      type="number"
      :value="value"
      @input="$emit('input', $event ? Number($event) : null)"
      :min="minValue"
      :max="maxValue"
      :step="step"
    />

    <!-- DATE -->
    <CInput
      v-else-if="tagDefinition.input_type === 'date'"
      type="date"
      :value="value"
      @input="$emit('input', $event)"
      :min="minDate"
      :max="maxDate"
    />

    <!-- CHECKBOX (multiple selection) -->
    <div v-else-if="tagDefinition.input_type === 'checkbox'" class="checkbox-group">
      <CInputCheckbox
        v-for="option in checkboxOptions"
        :key="option.value"
        :label="option.label"
        :checked="isChecked(option.value)"
        @update:checked="toggleCheckbox(option.value, $event)"
        class="mb-2"
      />
    </div>

    <!-- MULTISELECT (single selection in filters) -->
    <PSelectKeyValue
      v-else-if="tagDefinition.input_type === 'multiselect'"
      :value="value"
      @input="handleSelectInput"
      :options="multiselectOptions"
      label="label"
      track-by="value"
      allow-empty
    />

    <!-- TAGS (free-form with autocomplete) -->
    <PTagInput
      v-else-if="tagDefinition.input_type === 'tags'"
      :value="value || []"
      @input="$emit('input', $event)"
      :allowed_tags="suggestedTags"
      :addable="true"
    />
  </PFRow>
</template>

<script>
import PFRow from "@/domain/core/components/PFRow.vue";
import PSelectKeyValue from "@/domain/core/components/PSelectKeyValue.vue";
import PTagInput from "@/domain/core/components/PTagInput.vue";

export default {
  name: 'DynamicTagField',
  components: {
    PFRow,
    PSelectKeyValue,
    PTagInput
  },

  props: {
    tagDefinition: {
      type: Object,
      required: true
    },
    value: {
      type: [String, Number, Array],
      default: null
    }
  },

  computed: {
    selectOptions() {
      if (this.tagDefinition.input_type !== 'select') return []
      return this.tagDefinition.input_config?.options || []
    },

    // Safe getters for input_config properties with defaults
    placeholder() {
      return this.tagDefinition.input_config?.placeholder || ''
    },
    maxLength() {
      return this.tagDefinition.input_config?.max_length || null
    },
    minValue() {
      return this.tagDefinition.input_config?.min_value || null
    },
    maxValue() {
      return this.tagDefinition.input_config?.max_value || null
    },
    step() {
      return this.tagDefinition.input_config?.step || 1
    },
    minDate() {
      return this.tagDefinition.input_config?.min_date || null
    },
    maxDate() {
      return this.tagDefinition.input_config?.max_date || null
    },
    checkboxOptions() {
      return this.tagDefinition.input_config?.options || []
    },
    multiselectOptions() {
      return this.tagDefinition.input_config?.options || []
    },
    suggestedTags() {
      // Extract suggestions for tags input type, sorted by count descending
      if (this.tagDefinition.input_type === 'tags' && this.tagDefinition.suggestions) {
        return [...this.tagDefinition.suggestions]
          .sort((a, b) => b.count - a.count)
          .map(s => s.value)
      }
      return []
    }
  },

  methods: {
    handleSelectInput(option) {
      // PSelectKeyValue emits the full option object, we need just the value
      this.$emit('input', option ? option.value : null)
    },

    isChecked(optionValue) {
      if (!Array.isArray(this.value)) return false
      return this.value.includes(optionValue)
    },

    toggleCheckbox(optionValue, checked) {
      let newValue = Array.isArray(this.value) ? [...this.value] : []

      if (checked) {
        if (!newValue.includes(optionValue)) {
          newValue.push(optionValue)
        }
      } else {
        newValue = newValue.filter(v => v !== optionValue)
      }

      this.$emit('input', newValue.length > 0 ? newValue : null)
    }
  }
}
</script>

<style scoped>
.checkbox-group {
  display: flex;
  flex-direction: column;
}
</style>
